import React, { useState } from 'react';
import { Phone, MapPin, Clock, Send, Navigation } from 'lucide-react';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { toast } from 'sonner';

export default function ContactNew() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: 'Retour Affectif - Récupérer Son Ex',
    message: '',
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const services = [
    'Retour Affectif',
    'Récupérer Son Ex',
    'Voyance Amour',
    'Protection',
    'Désenvoûtement',
    'Chance'
  ];

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    setTimeout(() => {
      const submissions = JSON.parse(localStorage.getItem('contactSubmissions') || '[]');
      submissions.push({
        ...formData,
        timestamp: new Date().toISOString(),
      });
      localStorage.setItem('contactSubmissions', JSON.stringify(submissions));
      
      toast.success('Message envoyé avec succès !', {
        description: 'Vous serez contacté très prochainement.',
      });
      
      setFormData({
        name: '',
        email: '',
        phone: '',
        subject: 'Retour Affectif - Récupérer Son Ex',
        message: '',
      });
      setIsSubmitting(false);
    }, 1500);
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  return (
    <section id="contact" className="py-12 bg-black relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <Badge className="mb-4 bg-yellow-500/10 border-yellow-500/30 text-yellow-500">
            Consultation Découverte
          </Badge>
          <h2 className="text-4xl sm:text-5xl font-bold text-white mb-6">
            Contactez-nous
          </h2>
          <p className="text-lg text-gray-400 max-w-2xl mx-auto">
            <strong className="text-yellow-500">Première consultation DÉCOUVERTE</strong> et sans engagement. Spécialiste retour affectif.
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Left: Contact Info + Map */}
          <div className="lg:col-span-1 space-y-6">
            <Card className="p-6 bg-gradient-to-br from-gray-900 to-black border border-yellow-500/30">
              <h3 className="text-xl font-bold text-white mb-6">Informations de Contact</h3>
              
              <div className="space-y-4">
                <div className="flex items-start gap-4 p-4 bg-yellow-500/5 border border-yellow-500/20 rounded-lg">
                  <div className="w-12 h-12 rounded-xl bg-yellow-500/10 border border-yellow-500/30 flex items-center justify-center flex-shrink-0">
                    <Phone className="h-5 w-5 text-yellow-500" />
                  </div>
                  <div>
                    <div className="text-gray-400 text-sm mb-1">Téléphone</div>
                    <a href="tel:+33745449641" className="text-yellow-500 hover:text-yellow-400 font-semibold text-lg transition-colors">
                      +33 7 45 44 96 41
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4 p-4 rounded-lg">
                  <div className="w-12 h-12 rounded-xl bg-yellow-500/10 border border-yellow-500/30 flex items-center justify-center flex-shrink-0">
                    <MapPin className="h-5 w-5 text-yellow-500" />
                  </div>
                  <div>
                    <div className="text-gray-400 text-sm mb-1">Adresse</div>
                    <div className="text-white font-medium">Rue de la République</div>
                    <div className="text-gray-500 text-sm">13001<span className="seo-hidden"> Marseille</span></div>
                  </div>
                </div>

                <div className="flex items-start gap-4 p-4 rounded-lg">
                  <div className="w-12 h-12 rounded-xl bg-yellow-500/10 border border-yellow-500/30 flex items-center justify-center flex-shrink-0">
                    <Clock className="h-5 w-5 text-yellow-500" />
                  </div>
                  <div>
                    <div className="text-gray-400 text-sm mb-1">Horaires</div>
                    <div className="text-white font-medium">7j/7 de 8h à 23h30</div>
                    <div className="text-gray-500 text-sm">Réponse rapide garantie</div>
                  </div>
                </div>
              </div>

              <div className="mt-6 pt-6 border-t border-gray-800">
                <div className="flex items-center gap-2 mb-4">
                  <div className="flex gap-1">
                    {[...Array(5)].map((_, i) => (
                      <span key={i} className="text-yellow-500">⭐</span>
                    ))}
                  </div>
                  <span className="text-gray-400 text-sm">4.9/5 - 847 avis</span>
                </div>
                
                {/* Action Buttons */}
                <div className="flex flex-col gap-3">
                  <Button asChild className="w-full bg-yellow-500 hover:bg-yellow-600 text-black font-bold">
                    <a href="tel:+33745449641" className="flex items-center justify-center gap-2">
                      <Phone className="h-4 w-4" />
                      Appeler
                    </a>
                  </Button>
                  <Button asChild variant="outline" className="w-full border-green-500/50 text-green-500 hover:bg-green-500/10">
                    <a href="https://wa.me/33745449641" target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-2">
                      <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                      </svg>
                      WhatsApp
                    </a>
                  </Button>
                  <Button asChild className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold">
                    <a href="https://paypal.me/PierreMarie888" target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-2">
                      <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944 3.72a.77.77 0 0 1 .757-.629h6.724c2.332 0 4.053.64 5.108 1.88.947 1.108 1.283 2.609.997 4.457l-.002.013v.013c-.322 2.077-1.35 3.756-2.99 4.86-1.571 1.056-3.586 1.593-5.99 1.593H7.403a.77.77 0 0 0-.757.63l-.77 4.98a.641.641 0 0 1-.633.54h-.167v-.72zm11.755-14.79c-.006.046-.014.092-.022.138-.633 4.088-3.584 5.5-7.13 5.5H9.832a.872.872 0 0 0-.86.736l-.89 5.65-.253 1.6a.457.457 0 0 0 .451.53h3.165a.764.764 0 0 0 .754-.627l.031-.162.598-3.79.038-.208a.764.764 0 0 1 .754-.627h.474c3.072 0 5.477-1.248 6.179-4.86.293-1.51.141-2.77-.635-3.655a3.03 3.03 0 0 0-.868-.67z"/>
                      </svg>
                      Payer par PayPal
                    </a>
                  </Button>
                </div>
              </div>
            </Card>

            {/* Google Maps Integration */}
            <Card className="overflow-hidden bg-gradient-to-br from-gray-900 to-black border border-yellow-500/30">
              <div className="p-4 border-b border-yellow-500/20">
                <div className="flex items-center gap-2">
                  <Navigation className="h-5 w-5 text-yellow-500" />
                  <h4 className="text-white font-semibold">Notre emplacement</h4>
                </div>
              </div>
              <div className="relative">
                <iframe
                  title="Maître Diamma - Marabout Marseille Location"
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2903.8876456789!2d5.369780!3d43.296482!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x12c9c0c1c1c1c1c1%3A0x1234567890abcdef!2sRue%20de%20la%20R%C3%A9publique%2C%2013001%20Marseille!5e0!3m2!1sfr!2sfr!4v1234567890"
                  width="100%"
                  height="200"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  className="grayscale hover:grayscale-0 transition-all duration-500"
                />
              </div>
              <div className="p-4">
                <a 
                  href="https://www.google.com/maps/dir//Rue+de+la+R%C3%A9publique,+13001+Marseille"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 w-full py-2 px-4 bg-yellow-500/10 border border-yellow-500/30 rounded-lg text-yellow-500 hover:bg-yellow-500/20 transition-colors text-sm font-medium"
                >
                  <Navigation className="h-4 w-4" />
                  Obtenir l'itinéraire
                </a>
              </div>
            </Card>

            <Card className="p-6 bg-gradient-to-br from-yellow-500/5 to-yellow-600/5 border border-yellow-500/30">
              <h4 className="text-white font-semibold mb-4">Spécialités :</h4>
              <div className="flex flex-wrap gap-2">
                {services.map((service) => (
                  <Badge 
                    key={service}
                    variant="outline" 
                    className="border-yellow-500/30 text-yellow-500"
                  >
                    {service}
                  </Badge>
                ))}
              </div>
            </Card>

            <div className="p-6 bg-gradient-to-br from-gray-900 to-black border border-yellow-500/30 rounded-xl text-center">
              <h4 className="text-xl font-semibold text-white mb-3">Besoin d'un Retour Affectif Rapide ?</h4>
              <p className="text-gray-400 text-sm mb-4">
                Appelez maintenant pour une consultation immédiate. Récupérez votre ex en 7 jours.
              </p>
              <div className="flex flex-col gap-3">
                <Button asChild className="w-full bg-yellow-500 hover:bg-yellow-600 text-black font-bold">
                  <a href="tel:+33745449641" className="flex items-center justify-center gap-2">
                    <Phone className="h-4 w-4" />
                    Appeler
                  </a>
                </Button>
                <Button asChild variant="outline" className="w-full border-yellow-500/50 text-yellow-500 hover:bg-yellow-500/10">
                  <a href="https://wa.me/33745449641" target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-2">
                    WhatsApp
                  </a>
                </Button>
                <Button asChild className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold">
                  <a href="https://paypal.me/PierreMarie888" target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-2">
                    <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944 3.72a.77.77 0 0 1 .757-.629h6.724c2.332 0 4.053.64 5.108 1.88.947 1.108 1.283 2.609.997 4.457l-.002.013v.013c-.322 2.077-1.35 3.756-2.99 4.86-1.571 1.056-3.586 1.593-5.99 1.593H7.403a.77.77 0 0 0-.757.63l-.77 4.98a.641.641 0 0 1-.633.54h-.167v-.72zm11.755-14.79c-.006.046-.014.092-.022.138-.633 4.088-3.584 5.5-7.13 5.5H9.832a.872.872 0 0 0-.86.736l-.89 5.65-.253 1.6a.457.457 0 0 0 .451.53h3.165a.764.764 0 0 0 .754-.627l.031-.162.598-3.79.038-.208a.764.764 0 0 1 .754-.627h.474c3.072 0 5.477-1.248 6.179-4.86.293-1.51.141-2.77-.635-3.655a3.03 3.03 0 0 0-.868-.67l.003.005-.002-.003-.002-.002a.018.018 0 0 0-.005-.003l-.003-.002-.003-.002-.003-.001-.002-.002-.003-.001-.002-.002-.003-.001-.002-.002h-.002l-.002-.002-.002-.001-.002-.001-.002-.001-.002-.002h-.002l-.001-.002h-.002l-.002-.001-.001-.001-.002-.001-.001-.001-.002-.001-.001-.001-.001-.001-.002-.001H18.83l-.001-.001z"/>
                    </svg>
                    Payer par PayPal
                  </a>
                </Button>
              </div>
            </div>
          </div>

          {/* Right: Contact Form */}
          <div className="lg:col-span-2">
            <Card className="p-8 bg-gradient-to-br from-gray-900 to-black border border-yellow-500/30">
              <h3 className="text-2xl font-bold text-white mb-2">Demande de Consultation</h3>
              <p className="text-gray-400 mb-6">Décrivez votre situation pour une réponse personnalisée.</p>

              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="grid md:grid-cols-2 gap-5">
                  <div>
                    <Label htmlFor="name" className="text-gray-300 mb-2 block">Nom *</Label>
                    <Input
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      placeholder="Votre nom"
                      className="bg-black/50 border-yellow-500/30 text-white placeholder:text-gray-500 focus:border-yellow-500"
                    />
                  </div>

                  <div>
                    <Label htmlFor="phone" className="text-gray-300 mb-2 block">Téléphone</Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="06 12 34 56 78"
                      className="bg-black/50 border-yellow-500/30 text-white placeholder:text-gray-500 focus:border-yellow-500"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="email" className="text-gray-300 mb-2 block">Email *</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    placeholder="votre@email.com"
                    className="bg-black/50 border-yellow-500/30 text-white placeholder:text-gray-500 focus:border-yellow-500"
                  />
                </div>

                <div>
                  <Label htmlFor="subject" className="text-gray-300 mb-2 block">Sujet *</Label>
                  <select
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    required
                    className="w-full h-10 rounded-md border border-yellow-500/30 bg-black/50 text-white px-3 py-2 text-sm focus:border-yellow-500 focus:outline-none focus:ring-1 focus:ring-yellow-500"
                  >
                    <option value="Retour Affectif - Récupérer Son Ex">Retour Affectif - Récupérer Son Ex</option>
                    <option value="Voyance Amour">Voyance Amour</option>
                    <option value="Désenvoûtement">Désenvoûtement</option>
                    <option value="Protection Spirituelle">Protection Spirituelle</option>
                    <option value="Chance & Réussite">Chance & Réussite</option>
                    <option value="Autre demande">Autre demande</option>
                  </select>
                </div>

                <div>
                  <Label htmlFor="message" className="text-gray-300 mb-2 block">Votre situation *</Label>
                  <Textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={6}
                    placeholder="Décrivez votre situation en quelques mots..."
                    className="bg-black/50 border-yellow-500/30 text-white placeholder:text-gray-500 focus:border-yellow-500 resize-none"
                  />
                </div>

                <Button 
                  type="submit" 
                  disabled={isSubmitting}
                  className="w-full bg-yellow-500 hover:bg-yellow-600 text-black font-bold py-6"
                  size="lg"
                >
                  {isSubmitting ? (
                    'Envoi en cours...'
                  ) : (
                    <span className="flex items-center justify-center gap-2">
                      <Send className="w-5 h-5" />
                      Envoyer ma Demande
                    </span>
                  )}
                </Button>

                <p className="text-xs text-center text-gray-500">
                  Discrétion absolue garantie - Vos informations restent confidentielles
                </p>
              </form>
            </Card>

            {/* Trust Badges */}
            <div className="mt-6 grid grid-cols-3 gap-4">
              <div className="p-4 bg-gradient-to-br from-gray-900 to-black border border-yellow-500/20 rounded-xl text-center">
                <div className="text-2xl mb-1">🔒</div>
                <p className="text-xs text-gray-400">100% Confidentiel</p>
              </div>
              <div className="p-4 bg-gradient-to-br from-gray-900 to-black border border-yellow-500/20 rounded-xl text-center">
                <div className="text-2xl mb-1">⚡</div>
                <p className="text-xs text-gray-400">Réponse Rapide</p>
              </div>
              <div className="p-4 bg-gradient-to-br from-gray-900 to-black border border-yellow-500/20 rounded-xl text-center">
                <div className="text-2xl mb-1">✨</div>
                <p className="text-xs text-gray-400">30 ans d'exp.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
